// The word sheet: the correction ladder for one tapped word, one rung at a
// time (plan § Getting the right word). It shows the word letter by letter,
// plays it, asks "is that what you said?", and then offers whatever rung the
// ladder says is next among the rungs this host renders. "Save it for a
// grown-up" is always one tap away. Two rungs are seams for later branches:
// `rungs.type` (the dynamic keyboard) and `rungs["tell-me-about-it"]`.
import {
  type LadderRung,
  type LadderState,
  type UnresolvedToken,
  type WordToken,
} from "@bindery/composition";
import { Check, Mic, Square, UserRound, Volume2, X } from "lucide-react";
import { type ReactNode, useState } from "react";
import { ComposeSheet, ComposeSheetTitle } from "./compose-sheet";
import { offersConfirm } from "./ladder";

export interface RungContext {
  readonly token: WordToken;
  /** Commits a correctly spelled replacement; the host records the rung's origin. */
  readonly commit: (to: string) => void;
  readonly skip: () => void;
}

export type RungRenderers = Partial<
  Record<
    Extract<LadderRung, "type" | "tell-me-about-it">,
    (ctx: RungContext) => ReactNode
  >
>;

export interface WordSheetProps {
  readonly token: WordToken | UnresolvedToken;
  readonly ladder: LadderState;
  readonly offered: LadderRung | null;
  readonly alternatives: readonly string[];
  readonly hasClip: boolean;
  /** The microphone is open for this word's re-say. */
  readonly listening: boolean;
  readonly thinking: boolean;
  /** Some other recording is open or being written down; the mic waits. */
  readonly busy: boolean;
  readonly level: number;
  readonly rungs?: RungRenderers;
  readonly onHear: () => void;
  readonly onYes: () => void;
  readonly onNotQuite: () => void;
  readonly onHearAlternative: (word: string) => void;
  readonly onChoose: (word: string) => void;
  readonly onNoneOfThese: () => void;
  readonly onReSay: () => void;
  readonly onStopReSay: () => void;
  readonly onTypeIt: (to: string) => void;
  readonly onSkipRung: (rung: "type") => void;
  readonly onSave: () => void;
  readonly onClose: () => void;
}

const Letters = ({ text }: { text: string }) => (
  <div className="compose-letters" aria-label={text}>
    {Array.from(text).map((letter, i) => (
      <span
        key={`${String(i)}-${letter}`}
        className="compose-letter"
        aria-hidden
      >
        {letter}
      </span>
    ))}
  </div>
);

const Prompt = ({ children }: { children: ReactNode }) => (
  <p className="m-0 text-center text-xl" style={{ color: "var(--ink-soft)" }}>
    {children}
  </p>
);

function Alternatives({
  words,
  onHear,
  onChoose,
  onNone,
}: {
  words: readonly string[];
  onHear: (word: string) => void;
  onChoose: (word: string) => void;
  onNone: () => void;
}) {
  const [picked, setPicked] = useState<string | null>(null);
  return (
    <div className="flex flex-col items-center gap-3">
      <Prompt>Tap a word to hear it. Which one did you say?</Prompt>
      <div className="flex flex-wrap justify-center gap-3">
        {words.map((word) => (
          <button
            key={word}
            type="button"
            className="compose-btn"
            data-tone="hear"
            data-picked={picked === word ? "true" : undefined}
            onClick={() => {
              setPicked(word);
              onHear(word);
            }}
          >
            <Volume2 className="size-6" /> {word}
          </button>
        ))}
      </div>
      <div className="flex flex-wrap justify-center gap-3">
        <button
          type="button"
          className="compose-btn"
          data-tone="yes"
          disabled={picked === null}
          onClick={() => picked !== null && onChoose(picked)}
        >
          <Check className="size-7" /> That one
        </button>
        <button type="button" className="compose-btn" onClick={onNone}>
          None of these
        </button>
      </div>
    </div>
  );
}

function Rung(props: WordSheetProps & { readonly word: WordToken }) {
  const { offered, word } = props;
  switch (offered) {
    case "hear-and-compare":
      return (
        <div className="flex flex-col items-center gap-3">
          <Prompt>Is that what you said?</Prompt>
          <div className="flex flex-wrap justify-center gap-3">
            <button
              type="button"
              className="compose-btn"
              data-tone="yes"
              onClick={props.onYes}
            >
              <Check className="size-7" /> Yes
            </button>
            <button
              type="button"
              className="compose-btn"
              onClick={props.onNotQuite}
            >
              Not quite
            </button>
          </div>
        </div>
      );
    case "choose":
      return (
        <Alternatives
          words={props.alternatives}
          onHear={props.onHearAlternative}
          onChoose={props.onChoose}
          onNone={props.onNoneOfThese}
        />
      );
    case "re-say":
      return (
        <div className="flex flex-col items-center gap-3">
          <Prompt>
            {props.listening
              ? "Say the whole sentence again — tap when you're done"
              : props.thinking
                ? "Writing it down…"
                : "Say the whole sentence again"}
          </Prompt>
          <button
            type="button"
            className="compose-btn"
            data-tone="talk"
            disabled={props.thinking || (props.busy && !props.listening)}
            style={{ "--level": props.level } as React.CSSProperties}
            onClick={props.listening ? props.onStopReSay : props.onReSay}
          >
            {props.listening ? (
              <Square className="size-7" />
            ) : (
              <Mic className="size-7" />
            )}
            {props.listening ? "Done" : "Say it again"}
          </button>
        </div>
      );
    case "type": {
      const render = props.rungs?.type;
      return render === undefined
        ? null
        : render({
            token: word,
            commit: props.onTypeIt,
            skip: () => props.onSkipRung("type"),
          });
    }
    case "tell-me-about-it": {
      const render = props.rungs?.["tell-me-about-it"];
      return render === undefined
        ? null
        : render({ token: word, commit: props.onChoose, skip: props.onSave });
    }
    case "save-for-adult":
      return <Prompt>Let&rsquo;s save this one for a grown-up.</Prompt>;
    case null:
      return null;
  }
}

export function WordSheet(props: WordSheetProps) {
  const { token, ladder } = props;
  return (
    <ComposeSheet className="flex flex-col gap-5" onClose={props.onClose}>
      <ComposeSheetTitle className="sr-only">This word</ComposeSheetTitle>
      <div className="flex items-start justify-between">
        <button
          type="button"
          className="compose-icon"
          data-tone="hear"
          aria-label="Hear it"
          disabled={token.kind === "unresolved" && !props.hasClip}
          onClick={props.onHear}
        >
          <Volume2 className="size-7" />
        </button>
        <button
          type="button"
          className="compose-icon"
          aria-label="Close"
          onClick={props.onClose}
        >
          <X className="size-7" />
        </button>
      </div>

      {token.kind === "word" ? (
        <Letters text={token.text} />
      ) : (
        <div className="flex flex-col items-center gap-2">
          <UserRound
            className="size-16"
            style={{ color: "var(--plum-deep)" }}
          />
          <Prompt>A grown-up will help with this word.</Prompt>
        </div>
      )}

      {token.kind === "word" ? (
        ladder.kind === "open" ? (
          <Rung {...props} word={token} />
        ) : ladder.kind === "resolved" ? (
          <Prompt>That&rsquo;s your word.</Prompt>
        ) : (
          <Prompt>A grown-up will help with this word.</Prompt>
        )
      ) : null}

      {token.kind === "word" && offersConfirm(ladder, props.offered) ? (
        <div className="flex justify-center">
          <button
            type="button"
            className="compose-btn"
            data-tone="yes"
            style={{ fontSize: "1.1rem", minHeight: "3.25rem" }}
            onClick={props.onYes}
          >
            <Check className="size-6" /> Yes, that&rsquo;s my word
          </button>
        </div>
      ) : null}

      {token.kind === "word" && ladder.kind === "open" ? (
        <div className="flex justify-center">
          <button
            type="button"
            className="compose-btn"
            data-tone="grownup"
            style={{ fontSize: "1.1rem", minHeight: "3.25rem" }}
            onClick={props.onSave}
          >
            <UserRound className="size-6" /> Save it for a grown-up
          </button>
        </div>
      ) : null}
    </ComposeSheet>
  );
}
